// opervex-frontend/src/pages/ServiceManagement/ServiceOrder.js

import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Container, Row, Col, Form, Table, InputGroup, Card, Button } from "react-bootstrap";
import { FaSearch, FaCloudDownloadAlt, FaSave, FaPlus, FaTrash } from "react-icons/fa";
import { RiErrorWarningLine } from "react-icons/ri";
import { MdOutlineCancel } from "react-icons/md";
import { SearchHelpModal } from "../../utils/SearchHelp";
import { SelectModal } from "../../utils/SelectModal";
import OpervexStorage from "../../utils/OpervexStorage";
import ReasonPromptService from "../../services/ReasonPromptService";

import useOpervex from "../../hooks/useOpervex";
import ConfirmService from "../../components/ConfirmationModal/ConfirmService";
import QuantityCustom from "../../components/CustomInput/customQuantity";
import MaskedFormControl from "react-bootstrap-maskedinput";
import { useLoading } from "../../contexts/LoadingContext";

const rolePageRequire = "ORDER_SERVICE";

const dataDefault = {
  header: {
    id: 0,
    protocol: "",
    idTask: 0,
    customerId: 0,
    customerName: "",
    branch: 0,
    city: 0,
    documentDate: "",
    startTime: "",
    endTime: "",
    link: "",
    technician: 0,
    supervisor: 0,
    assistant: 0,
    unproductive: false,
    gmId: 0,
    deposit: 0,
  },

  services: [],

  products: [],
};

const actionsDefault = {
  create: true,
  change: true,
  delete: true,
  save: true,
  reversal: true,
};

const ServiceOrder = () => {
  const originalValues = useRef({});
  const [prices, setPrices] = useState([]);
  const [cities, setCities] = useState([]);
  const [branches, setBranches] = useState([]);
  const [data, setData] = useState(dataDefault);
  const [disabledField, setDisabledField] = useState(true);
  const [actions, setActions] = useState(actionsDefault);
  const [errors, setErrors] = useState(null);
  const { roles, loadingGlobal } = useAuth();
  const { id } = useParams();
  const OpervexAPI = useOpervex();

  const { setLoadingScreen } = useLoading();

  const fetchData = async () => {
    setBranches(await OpervexAPI.Accounting.Branches.findAll());
    setCities(await OpervexAPI.Accounting.Cities.findAll());

    await commandAction({ ...actionsDefault, create: false });

    if (id) await searchAPI(id);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loadingGlobal) return <div>Carregando...</div>;

  const commandAction = (actions) => {
    const hasPermission = roles.find((role) => role.command === rolePageRequire);

    if (!hasPermission) {
      setActions(actionsDefault);
      return;
    }

    if (!actions.create) actions.create = !hasPermission.actions.some((action) => action.key === "Create");
    if (!actions.change) actions.change = !hasPermission.actions.some((action) => action.key === "Change");
    if (!actions.reversal) actions.reversal = !hasPermission.actions.some((action) => action.key === "Reversal");

    setActions({ ...actions });
  };

  const searchAPI = async (id) => {
    setDisabledField(true);

    if (!id) return;

    var result = await OpervexAPI.ServiceManagement.ServiceOrder.findId(id);

    if (result && !result.error && !result.errors) {
      setData({
        ...dataDefault,
        ...result,
        header: { ...dataDefault.header, ...(result.header || {}) },
        services: result.services || [],
        products: result.products || [],
      });
      commandAction({ ...actionsDefault, create: false, change: false, reversal: false });
    } else {
      setData(dataDefault);
      commandAction({ ...actionsDefault, create: false });
      alert(result.error ? result.error : result.errors ? result.errors[0].message : "");
    }

    setErrors(null);
  };

  const handleButtonSearch = async () => {
    const dataStart = new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split("T")[0];

    const item = await SearchHelpModal({
      urlData: "/ServiceManagement/ServiceOrder/SearchHelp",
      preFilters: [{ fieldname: "documentDate", value: dataStart, operator: ">=" }],
      schema: await OpervexAPI.ServiceManagement.ServiceOrder.schema(),
    });

    if (item && item.id) await searchAPI(item.id);
  };

  const handleButtonOpenGM = async (id) => {
    const url = `/${OpervexStorage.getTenantIdFromPath()}/MaterialManagement/GoodsMovement/${id}`;
    window.open(url, "_blank");
  };

  const handleButtonCreate = async () => {
    setErrors(null);
    setData(dataDefault);
    commandAction({ ...actionsDefault, save: false });
    setDisabledField(false);
  };

  const handleButtonCancel = async () => {
    setErrors(null);

    if (!data.header.id) {
      setData(dataDefault);
      setDisabledField(true);
      commandAction({ ...actionsDefault, create: false });
    } else {
      await searchAPI(data.header.id);
    }
  };

  const handleButtonSave = async () => {
    var body = { ...data };

    if (body.header.id) {
      const result = await OpervexAPI.ServiceManagement.ServiceOrder.update(body.header.id, body);

      if (result.errors) {
        setErrors(result.errors);
        await ConfirmService.show("Avisos", result.errors, true);
        return;
      }

      await searchAPI(data.header.id);
      alert("Registro atualizado com sucesso!");
    } else {
      const result = await OpervexAPI.ServiceManagement.ServiceOrder.create(body);

      if (result.errors) {
        setErrors(result.errors);
        await ConfirmService.show("Avisos", result.errors, true);
        return;
      }

      await searchAPI(result.id);

      alert("Registro criado com sucesso!");
    }

    setErrors(null);
  };

  const handleButtonReversal = async () => {
    let confirm = await ConfirmService.show("Estorno", "Deseja proseguir com o estorno?");
    if (!confirm) return;

    const reason = await ReasonPromptService.show("Motivo do estorno", "Por favor, informe o motivo:");
    if (!reason) {
      alert("Operação cancelada. É necessário informar um motivo.");
      return;
    }

    const result = await OpervexAPI.ServiceManagement.ServiceOrder.reversal(data.header.id, { reason });

    if (result.errors) {
      await ConfirmService.show("Avisos", result.errors, true);
      return;
    }

    await searchAPI(result.id);
  };

  const hiddenButtonReversal = () => {
    if (!actions.reversal) {
      if (data?.header?.reversalType) return true;
    }

    return actions.reversal;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    const group = name.split(".")[0];
    const field = name.split(".")[1];

    switch (group) {
      case "header":
        setData((prev) => ({ ...prev, header: { ...prev.header, [field]: type === "checkbox" ? checked : value } }));
        break;

      default:
        break;
    }

    if (name === "header.branch") updateServicesPrices(value);
  };

  const updateServicesPrices = (branch) => {
    setData((prev) => ({
      ...prev,
      services: prev.services.map((row) => {
        const price = prices.find((x) => x.service === row.idItem && x.branch === Number(branch)) || {};

        return {
          ...row,
          price: price.price,
          priceResale: price.priceResale,
          priceOs: price.priceOs,
          points: price.points,
        };
      }),
    }));
  };

  const handleButtonLink = async () => {
    if (disabledField) return;

    const link = data.header.link;

    setLoadingScreen(true);
    const result = await OpervexAPI.Unifique.Services.getDataByURL(data.header.link);
    setLoadingScreen(false);

    if (result.errors) {
      await ConfirmService.show("Avisos", result.errors, true);
      return;
    }

    /*
     * dataDefault contém objetos e listas aninhados. A cópia superficial
     * reutilizava as listas de serviços e produtos e os itens eram inseridos
     * novamente a cada clique no botão do link.
     */
    let newData = {
      ...dataDefault,
      header: { ...dataDefault.header },
      products: [],
      services: [],
    };

    newData.header.link = link;
    newData.header.protocol = result.summary.protocol;
    newData.header.idTask = result.summary.idTask;
    newData.header.documentDate = result.summary.startDate;
    newData.header.startTime = result.summary.startTime?.slice(0, 5);
    newData.header.endTime = result.summary.endTime?.slice(0, 5);
    newData.header.customerId = result.summary.IdAssyst;
    newData.header.customerName = result.customer?.name;

    newData.header.city = result.summary.city?.id;

    newData.header.technician = result.technician?.id ?? 0;
    newData.header.technicianName = result.technician?.fullName ?? "";
    newData.header.deposit = result.technician?.storageId;

    newData.header.supervisor = result.supervisor?.id ?? 0;
    newData.header.supervisorName = result.supervisor?.fullName ?? "";

    if (result.items) {
      result.items.forEach((row, index) => {
        newData.products.push({
          item: index + 1,
          idItem: row.product,
          description: row.description,
          unitMeasurement: row.unitMeasurement,
          controlSerial: row.controlSerial,
          controlMac: row.controlMac,

          quantity: row.quantity,
          serialNumber: row.serialNumber,
          mac: row.mac,
        });
      });
    }

    if (result.services) {
      for (const [index, row] of result.services.entries()) {
        newData.services.push({
          item: index + 1,
          idItem: row.id,
          description: row.description,
          quantity: 1,
          price: 0,
          priceResale: 0,
          priceOs: 0,
          points: 0,
        });

        const service = await OpervexAPI.ServiceManagement.Service.findId(row.id);

        if (service?.prices) addUniquePrices(service.prices);
      }
    }

    setData(newData);
  };

  const handleSelectEmployee = async (type, id) => {
    if (disabledField) return;

    let employee = {};

    if (id) {
      employee = await OpervexAPI.HumanResources.Employees.findIdFilter(id);
    } else {
      employee = await SearchHelpModal({
        urlData: "/HumanResources/Employees/SearchHelp",
        schema: await OpervexAPI.HumanResources.Employees.schema(),
        preFilters: [{ fieldname: "disabled", value: false }],
      });
    }

    if (!employee) return;

    let newData = { ...data };

    switch (type) {
      case "technician":
        newData.header.technician = employee.id;
        newData.header.technicianName = `${employee.firstName} ${employee.lastName}`;

        /*
         * Quando o técnico é informado pelo código, "employee" já foi
         * carregado pelo findIdFilter acima. Reutilizamos essa resposta para
         * evitar uma segunda consulta ao sair do campo.
         *
         * Pela pesquisa modal, carregamos os detalhes uma única vez, pois o
         * resultado da pesquisa pode não incluir supervisor e depósito.
         */
        const technician = id ? employee : await OpervexAPI.HumanResources.Employees.findIdFilter(employee.id);
        if (technician) {
          if (technician?.seniorManager) {
            newData.header.supervisor = technician.seniorManager.id;

            newData.header.supervisorName = `${technician.seniorManager?.firstName} ${technician.seniorManager?.lastName}`;
          }

          newData.header.deposit = technician?.storageId;
        }
        break;
      case "supervisor":
        newData.header.supervisor = employee.id;
        newData.header.supervisorName = `${employee.firstName} ${employee.lastName}`;
        break;
      case "assistant":
        newData.header.assistant = employee.id;
        newData.header.assistantName = `${employee.firstName} ${employee.lastName}`;
        break;
      default:
        break;
    }

    setData(newData);
  };

  const handleSelectService = async (obj) => {
    let item = null;

    if (obj.onClick) {
      item = await SearchHelpModal({
        urlData: "/ServiceManagement/Service/SearchHelp",
        schema: await OpervexAPI.ServiceManagement.Service.schema(),
        preFilters: [{ fieldname: "disabled", value: false }],
      });
    } else item = obj;

    if (item && item.id) await addServiceById(item.id, obj.index);
  };

  const addServiceById = async (id, index) => {
    let service = null;

    service = await OpervexAPI.ServiceManagement.Service.findId(id);

    if (!service || service.error) return alert("Serviço inválido");

    const price = service.prices.find((x) => x.branch === Number(data.header.branch)) || {};

    addUniquePrices(service.prices);

    setData((prev) => ({
      ...prev,
      services: prev.services.map((row, i) =>
        i === index
          ? {
              ...row,
              idItem: service.id,
              description: service.description,
              price: price.price,
              priceResale: price.priceResale,
              priceOs: price.priceOs,
              points: price.points,
            }
          : row,
      ),
    }));
  };

  // Suponha que service.prices seja um array de objetos
  // e que você queira evitar duplicatas com base em 'id'
  const addUniquePrices = (servicePrices) => {
    setPrices((prevPrices) => {
      const newPrices = servicePrices.filter(
        (newPrice) => !prevPrices.some((existing) => existing.service === newPrice.service && existing.branch === newPrice.branch),
      );
      return [...prevPrices, ...newPrices];
    });
  };

  const handleSelectProduct = async (obj) => {
    if (disabledField) return;

    let product = null;
    let item = null;

    if (obj.onClick) {
      if (data.header.deposit) {
        item = await SearchHelpModal({
          urlData: "/MaterialManagement/Stock/SearchHelp",
          schema: await OpervexAPI.MaterialManagement.Stock.schema(),
          preFilters: [
            { fieldname: "deposit.id", value: data.header.deposit },
            { fieldname: "product.disabled", value: false },
          ],
        });
      } else {
        item = await SearchHelpModal({
          urlData: "/MaterialManagement/Product/SearchHelp",
          schema: await OpervexAPI.MaterialManagement.Products.schema(),
          preFilters: [{ fieldname: "disabled", value: false }],
        });
      }
    } else item = obj;

    if (item && item.id) product = await OpervexAPI.MaterialManagement.Products.findId(item.id);

    if (!product || product.error) return alert("Produto inválido");

    if (product) {
      const quantity = product.controlSerial || product.controlMac ? 1 : 0;

      setData((prev) => ({
        ...prev,
        products: prev.products.map((row, i) =>
          i === obj.index
            ? {
                ...row,
                idItem: product.id,
                description: product.description,
                unitMeasurement: product.unitMeasurement,
                controlSerial: product.controlSerial,
                controlMac: product.controlMac,
                quantity: quantity,
              }
            : row,
        ),
      }));
    }
  };

  const handleChangeRow = async (index, e) => {
    const { name, value } = e.target;

    let _value = value;

    const group = name.split(".")[0];
    const field = name.split(".")[1];

    if (field === "unitMeasurement") _value = _value.toUpperCase();

    switch (group) {
      case "product":
        setData((prev) => ({
          ...prev,
          products: prev.products.map((row, i) => (i === index ? { ...row, [field]: _value } : row)),
        }));
        break;
      case "service":
        setData((prev) => ({
          ...prev,
          services: prev.services.map((row, i) => (i === index ? { ...row, [field]: _value } : row)),
        }));
        break;
      default:
        break;
    }
  };

  const handleFocus = (event, index, name, row) => {
    originalValues.current[`${index}_${name}`] = event.target.value;
  };

  const handleBlur = (event, index, name, row) => {
    const originalValue = originalValues.current[`${index}_${name}`];
    const currentValue = event.target.value;

    if (originalValue === currentValue) return;

    switch (name) {
      case "service.idItem":
        handleSelectService({ index: index, id: row ? row.idItem : null, onClick: false });
        break;
      case "product.idItem":
        handleSelectProduct({ index: index, id: row ? row.idItem : null, onClick: false });
        break;
      case "header.technician":
        handleSelectEmployee("technician", data.header.technician);
        break;
      case "header.supervisor":
        handleSelectEmployee("supervisor", data.header.supervisor);
        break;
      case "header.assistant":
        handleSelectEmployee("assistant", data.header.assistant);
        break;
      default:
        break;
    }
  };

  const handleAddRow = async (process) => {
    switch (process) {
      case "product":
        setData((prev) => ({
          ...prev,
          products: [
            ...prev.products,
            {
              item: data.products.length + 1,
              idItem: "",
              description: "",
              quantity: 0,
              unitMeasurement: "",
              controlSerial: false,
              controlMac: false,
              serialNumber: "",
              mac: "",
            },
          ],
        }));
        break;
      case "service":
        setData((prev) => ({
          ...prev,
          services: [
            ...prev.services,
            {
              item: data.services.length + 1,
              idItem: "",
              description: "",
              quantity: 1,
              priceUnit: 0,
              price: 0,
              priceResale: 0,
              priceOs: 0,
            },
          ],
        }));
        break;
      default:
        break;
    }
  };

  const handleRemoveRow = (process, index) => {
    switch (process) {
      case "product":
        setData((prev) => {
          const updatedItems = prev.products.filter((_, i) => i !== index);
          const reindexedItems = updatedItems.map((row, i) => ({
            ...row,
            item: i + 1,
          }));

          return {
            ...prev,
            products: reindexedItems,
          };
        });
        break;
      case "service":
        setData((prev) => {
          const updatedItems = prev.services.filter((_, i) => i !== index);
          const reindexedItems = updatedItems.map((row, i) => ({
            ...row,
            item: i + 1,
          }));

          return {
            ...prev,
            services: reindexedItems,
          };
        });
        break;
      default:
        break;
    }
  };

  const checkRowControl = (row, field) => {
    if (disabledField) return true;

    switch (field) {
      case "serial":
        if (row.controlSerial) return false;
        break;
      case "mac":
        if (row.controlMac) return false;

        break;
      default:
        if (row.controlSerial || row.controlMac) return false;
        break;
    }

    return true;
  };

  const handleSelectSerial = async (index, row) => {
    if (!data.header.deposit) return;

    const results = await OpervexAPI.MaterialManagement.StockSerial.findByProductDeposit(row.idItem, data.header.deposit);

    let items = [];

    results?.forEach((result) => {
      items.push({
        serialNumber: result.serialNumber,
        mac: result.mac,
        selected: false,
      });
    });

    let selected = await SelectModal({
      title: "Seleção de Nº Séries/MAC",
      multiple: false,
      fields: [
        { key: "serialNumber", description: "Nº Serial" },
        { key: "mac", description: "MAC" },
      ],
      items: items,
    });

    if (!selected) return;

    setData((prev) => ({
      ...prev,
      products: prev.products.map((row, i) =>
        i === index ? { ...row, serialNumber: selected.serialNumber, mac: selected.mac } : row,
      ),
    }));
  };

  const handleButtonLog = async () => {
    await ConfirmService.show("Log", errors, true);
  };

  return (
    <>
      <Container className="mt-2">
        <Form>
          {/* Informações da O.S. */}
          <Card className="mb-2">
            <Card.Header>
              <Row className="d-flex justify-content-between">
                <Col md={8}>
                  <h5>Informações da O.S.</h5>
                </Col>
                <Col md={4} className="text-end">
                  <Button variant="success" size="sm" className="me-2" onClick={handleButtonCreate} hidden={actions.create}>
                    <FaPlus /> Novo
                  </Button>
                  {/* <Button
                    variant="primary"
                    size="sm"
                    className="me-2"
                    onClick={handleButtonChange}
                    hidden={actions.change || hiddenButtonReversal()}
                  >
                    <FaPencilAlt /> Alterar
                  </Button> */}
                  <Button variant="danger" size="sm" className="me-2" onClick={handleButtonCancel} hidden={actions.save}>
                    <MdOutlineCancel /> Cancelar
                  </Button>
                  <Button variant="primary" size="sm" className="me-2" onClick={handleButtonSave} hidden={actions.save}>
                    <FaSave /> Salvar
                  </Button>

                  <Button
                    variant="danger"
                    size="sm"
                    className="me-2"
                    onClick={handleButtonReversal}
                    hidden={hiddenButtonReversal()}
                  >
                    <FaSave /> Estornar
                  </Button>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body>
              <Row className="mb-2">
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Código</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        size="sm"
                        value={data?.header?.id ? data.header.id : ""}
                        name="header.id"
                        disabled={true}
                      />

                      <InputGroup.Text size="sm" type="button" onClick={handleButtonSearch}>
                        <FaSearch />
                      </InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Protocolo</Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      value={data.header.protocol ?? ""}
                      disabled={disabledField}
                      name="header.protocol"
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>ID Tarefa</Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      value={data.header.idTask ? data.header.idTask : ""}
                      disabled={disabledField}
                      name="header.idTask"
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Data Execução</Form.Label>
                    <Form.Control
                      type="date"
                      size="sm"
                      value={data.header.documentDate ?? ""}
                      disabled={disabledField}
                      name="header.documentDate"
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={1}>
                  <Form.Group>
                    <Form.Label>Início</Form.Label>
                    <Form.Control
                      type="time"
                      size="sm"
                      value={data.header.startTime ?? ""}
                      disabled={disabledField}
                      name="header.startTime"
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={1}>
                  <Form.Group>
                    <Form.Label>Fim</Form.Label>
                    <Form.Control
                      type="time"
                      size="sm"
                      value={data.header.endTime ?? ""}
                      disabled={disabledField}
                      name="header.endTime"
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-2">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Empresa/Filial</Form.Label>
                    <Form.Select
                      size="sm"
                      value={data.header.branch ?? ""}
                      name="header.branch"
                      onChange={handleChange}
                      disabled={disabledField}
                    >
                      <option>Selecione...</option>
                      {branches
                        ?.filter((branch) => disabledField || branch.disabled === false)
                        ?.map((branch) => (
                          <option key={branch.id} value={branch.id}>
                            {branch.description}
                          </option>
                        ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={2}>
                  <Form.Group>
                    <Form.Label>&nbsp;</Form.Label>
                    <Form.Check
                      type="checkbox"
                      label="Invalidar Pontuação"
                      id="header.unproductive"
                      checked={Boolean(data.header.unproductive)}
                      name="header.unproductive"
                      onChange={handleChange}
                      disabled={disabledField}
                      className="mb-0"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group hidden={!data.header.unproductive}>
                    <Form.Label>Motivo</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={data.header.reason ?? ""}
                      maxLength={150}
                      name="header.reason"
                      onChange={handleChange}
                      disabled={disabledField}
                      style={{ resize: "none" }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-2">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Link:</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        size="sm"
                        value={data.header.link ?? ""}
                        name="header.link"
                        onChange={handleChange}
                        disabled={disabledField}
                      />
                      <InputGroup.Text size="sm" type="button" onClick={handleButtonLink} disabled={disabledField}>
                        <FaCloudDownloadAlt />
                      </InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-2">
                <Col md={2}>
                  <Form.Group hidden={!data.header.gmId}>
                    <Form.Label>Movimentação</Form.Label>
                    <InputGroup>
                      <Form.Control type="text" size="sm" value={data.header.gmId ?? ""} name="header.gmId" disabled={true} />
                      <InputGroup.Text role="button" onClick={() => handleButtonOpenGM(data.header.gmId)}>
                        <FaSearch />
                      </InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={2} hidden={!(data.header.reversalType === 1)}>
                  <Form.Group>
                    <Form.Label>Documento de Estorno</Form.Label>
                    <InputGroup>
                      <Form.Control type="text" size="sm" value={data.header.reversal ?? ""} name="header.reversal" disabled />

                      <InputGroup.Text role="button" onClick={() => handleButtonOpenGM(data.header.reversal)}>
                        <FaSearch />
                      </InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={2} hidden={!(data.header.reversalType === 2)}>
                  <Form.Group>
                    <Form.Label>Documento Estornado</Form.Label>
                    <InputGroup>
                      <Form.Control type="text" size="sm" value={data.header.reversal ?? ""} name="header.reversal" disabled />

                      <InputGroup.Text role="button" onClick={() => handleButtonOpenGM(data.header.reversal)}>
                        <FaSearch />
                      </InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Dados do Cliente */}
          <Row className="mb-2">
            <Col md={6}>
              <Card className="mb-2">
                <Card.Header>
                  <Row className="d-flex justify-content-between">
                    <Col md={8}>
                      <h5>Dados do Cliente</h5>
                    </Col>
                    <Col md={4} className="text-end">
                      <Button variant="warning" size="sm" className="me-2" onClick={handleButtonLog} hidden={!errors}>
                        <RiErrorWarningLine /> Log
                      </Button>
                    </Col>
                  </Row>
                </Card.Header>
                <Card.Body>
                  <Row className="mb-2">
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Código</Form.Label>
                        <Form.Control
                          type="number"
                          size="sm"
                          value={data.header.customerId ? data.header.customerId : ""}
                          name="header.customerId"
                          onChange={handleChange}
                          disabled={disabledField}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={9}>
                      <Form.Group>
                        <Form.Label>Nome</Form.Label>
                        <Form.Control
                          type="text"
                          size="sm"
                          value={data.header.customerName ?? ""}
                          name="header.customerName"
                          onChange={handleChange}
                          disabled={disabledField}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Cidade</Form.Label>
                        <Form.Select
                          size="sm"
                          value={data.header.city ?? ""}
                          name="header.city"
                          onChange={handleChange}
                          disabled={disabledField}
                        >
                          <option>Selecione...</option>
                          {cities
                            ?.filter((city) => disabledField || city.disabled === false)
                            ?.map((city) => (
                              <option key={city.id} value={city.id}>
                                {city.name}
                              </option>
                            ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card className="mb-2">
                <Card.Header>
                  <h5>Equipe</h5>
                </Card.Header>
                <Card.Body>
                  {/* Técnico */}
                  <Row className="mb-2">
                    <Col md={3}>
                      <Form.Label>Técnico</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="text"
                          size="sm"
                          value={data.header.technician ? data.header.technician : ""}
                          name="header.technician"
                          onChange={handleChange}
                          disabled={disabledField}
                          onFocus={(event) => handleFocus(event, null, "header.technician", null)}
                          onBlur={(event) => handleBlur(event, null, "header.technician", null)}
                        />

                        <InputGroup.Text
                          size="sm"
                          type="button"
                          disabled={disabledField}
                          onClick={(e) => handleSelectEmployee("technician")}
                        >
                          <FaSearch />
                        </InputGroup.Text>
                      </InputGroup>
                    </Col>
                    <Col md={9}>
                      <Form.Group>
                        <Form.Label>Nome</Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            size="sm"
                            value={data.header.technicianName ? data.header.technicianName : ""}
                            name="header.technicianName"
                            disabled={true}
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Supervisor */}
                  <Row className="mb-2">
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Supervisor</Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            size="sm"
                            value={data.header.supervisor ? data.header.supervisor : ""}
                            name="header.supervisor"
                            onChange={handleChange}
                            disabled={disabledField}
                            onFocus={(event) => handleFocus(event, null, "header.supervisor", null)}
                            onBlur={(event) => handleBlur(event, null, "header.supervisor", null)}
                          />

                          <InputGroup.Text
                            size="sm"
                            type="button"
                            disabled={disabledField}
                            onClick={(e) => handleSelectEmployee("supervisor")}
                          >
                            <FaSearch />
                          </InputGroup.Text>
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    <Col md={9}>
                      <Form.Group>
                        <Form.Label>Nome</Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            size="sm"
                            value={data.header.supervisorName ?? ""}
                            name="header.supervisorName"
                            disabled={true}
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Auxiliar */}
                  <Row className="mb-2">
                    <Col md={3}>
                      <Form.Label>Auxiliar</Form.Label>
                      <Form.Group>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            size="sm"
                            value={data.header.assistant ? data.header.assistant : ""}
                            name="header.assistant"
                            onChange={handleChange}
                            disabled={disabledField}
                            onFocus={(event) => handleFocus(event, null, "header.assistant", null)}
                            onBlur={(event) => handleBlur(event, null, "header.assistant", null)}
                          />

                          <InputGroup.Text
                            size="sm"
                            type="button"
                            disabled={disabledField}
                            onClick={(e) => handleSelectEmployee("assistant")}
                          >
                            <FaSearch />
                          </InputGroup.Text>
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    <Col md={9}>
                      <Form.Group>
                        <Form.Label>Nome</Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            size="sm"
                            value={data.header.assistantName ?? ""}
                            name="header.assistantName"
                            disabled={true}
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Form>

        {/* Serviços Executados */}
        <Card className="mb-2">
          <Card.Header>
            <Row className="d-flex justify-content-between">
              <Col md={8}>
                <h5>Serviços Executados</h5>
              </Col>
              <Col md={4} className="text-end">
                <Button variant="primary" size="sm" onClick={() => handleAddRow("service")} hidden={actions.save}>
                  <FaPlus />
                </Button>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body>
            <Row className="mb-2">
              <Table striped bordered hover size="sm">
                <thead>
                  <tr className="text-center align-middle">
                    <th width="5%">Item</th>
                    <th width="13%">Código</th>
                    <th width="33%">Descrição</th>
                    <th width="9%">Quantidade</th>
                    <th width="10%">Preço</th>
                    <th width="10%">Preço Revenda</th>
                    <th width="10%">Preço OS</th>
                    <th width="5%">Pontos</th>
                    <th width="5%">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.services?.length > 0 ? (
                    data.services.map((row, index) => (
                      <tr key={index}>
                        <td className="text-center">{row.item}</td>
                        <td>
                          {disabledField ? (
                            row.idItem
                          ) : (
                            <InputGroup>
                              <Form.Control
                                type="text"
                                size="sm"
                                name="service.idItem"
                                value={row.idItem ?? ""}
                                onChange={(e) => handleChangeRow(index, e)}
                                disabled={disabledField}
                                onFocus={(event) => handleFocus(event, index, "service.idItem", row)}
                                onBlur={(event) => handleBlur(event, index, "service.idItem", row)}
                              />
                              <InputGroup.Text
                                size="sm"
                                type="button"
                                onClick={(e) => handleSelectService({ index: index, id: null, onClick: true })}
                              >
                                <FaSearch />
                              </InputGroup.Text>
                            </InputGroup>
                          )}
                        </td>
                        <td>{row.description}</td>
                        <td>
                          <QuantityCustom
                            value={row.quantity ?? ""}
                            onChange={(e) => handleChangeRow(index, { target: { name: "service.quantity", value: e } })}
                            disabled={disabledField}
                            showDisabled={false}
                            decimal={3}
                          />
                        </td>
                        <td>
                          {new Intl.NumberFormat("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          }).format(row.price ? row.price : 0)}
                        </td>
                        <td>
                          {new Intl.NumberFormat("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          }).format(row.priceResale ? row.priceResale : 0)}
                        </td>
                        <td>
                          {new Intl.NumberFormat("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          }).format(row.priceOs ? row.priceOs : 0)}
                        </td>
                        <td>
                          <QuantityCustom value={row.points ? row.points : 0} disabled={true} showDisabled={false} decimal={2} />
                        </td>
                        <td className="text-center">
                          {!disabledField && (
                            <Button variant="danger" onClick={() => handleRemoveRow("service", index)} size="sm">
                              <FaTrash />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center">
                        Nenhum serviço encontrado
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Row>
          </Card.Body>
        </Card>

        {/* Produtos */}
        <Card className="mb-2">
          <Card.Header>
            <Row className="d-flex justify-content-between">
              <Col md={8}>
                <h5>Produtos</h5>
              </Col>
              <Col md={4} className="text-end">
                <Button variant="primary" size="sm" onClick={() => handleAddRow("product")} hidden={actions.save}>
                  <FaPlus />
                </Button>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body>
            <Row className="mb-2">
              <Table striped bordered hover size="sm">
                <thead>
                  <tr className="text-center align-middle">
                    <th width="5%">Item</th>
                    <th width="15%">Código</th>
                    <th width="36%">Descrição</th>
                    <th width="6%">Quantidade</th>
                    <th width="5%">Unidade</th>
                    <th width="12%">Nº Série</th>
                    <th width="12%">MAC</th>
                    <th width="9%">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.products?.length > 0 ? (
                    data.products.map((row, index) => (
                      <tr key={index}>
                        <td className="text-center">{row.item}</td>
                        <td>
                          {disabledField ? (
                            row.idItem
                          ) : (
                            <InputGroup>
                              <Form.Control
                                type="text"
                                size="sm"
                                name="product.idItem"
                                value={row.idItem ?? ""}
                                onChange={(e) => handleChangeRow(index, e)}
                                disabled={disabledField}
                                onFocus={(event) => handleFocus(event, index, "product.idItem", row)}
                                onBlur={(event) => handleBlur(event, index, "product.idItem", row)}
                              />
                              <InputGroup.Text
                                size="sm"
                                type="button"
                                onClick={(e) => handleSelectProduct({ index: index, id: null, onClick: true })}
                              >
                                <FaSearch />
                              </InputGroup.Text>
                            </InputGroup>
                          )}
                        </td>
                        <td>{row.description}</td>
                        <td>
                          <QuantityCustom
                            value={row.quantity ?? ""}
                            onChange={(e) => handleChangeRow(index, { target: { name: "product.quantity", value: e } })}
                            disabled={!checkRowControl(row) ? true : disabledField}
                            showDisabled={false}
                            decimal={row.controlSerial ? 0 : 3}
                          />
                        </td>
                        <td className="text-center">
                          {disabledField || !checkRowControl(row) ? (
                            row.unitMeasurement
                          ) : (
                            <Form.Control
                              type="text"
                              size="sm"
                              name="product.unitMeasurement"
                              value={row.unitMeasurement ? row.unitMeasurement : ""}
                              onChange={(e) => handleChangeRow(index, e)}
                              disabled={disabledField}
                            />
                          )}
                        </td>

                        <td className="text-center">
                          {checkRowControl(row, "serial") ? (
                            row.serialNumber
                          ) : (
                            <Form.Control
                              type="text"
                              size="sm"
                              name="product.serialNumber"
                              value={row.serialNumber ?? ""}
                              onChange={(e) => handleChangeRow(index, e)}
                              className="rounded-0"
                            />
                          )}
                        </td>
                        <td className="text-center">
                          {checkRowControl(row, "mac") ? (
                            row.mac
                          ) : (
                            <MaskedFormControl
                              type="text"
                              size="sm"
                              mask="**:**:**:**:**:**"
                              name="product.mac"
                              value={row.mac ?? ""}
                              onChange={(e) => handleChangeRow(index, e)}
                              className="rounded-0"
                            />
                          )}
                        </td>
                        <td className="text-center">
                          {!checkRowControl(row) ? (
                            <Button
                              variant="secondary"
                              className={disabledField ? "" : "me-3"}
                              onClick={(e) => handleSelectSerial(index, row)}
                              size="sm"
                            >
                              <FaSearch />
                            </Button>
                          ) : (
                            <span className="me-3 d-inline-block" style={{ width: "32px" }}></span>
                          )}
                          {!disabledField ? (
                            <Button variant="danger" onClick={() => handleRemoveRow("product", index)} size="sm">
                              <FaTrash />
                            </Button>
                          ) : (
                            ""
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center">
                        Nenhum produto encontrado
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Row>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default ServiceOrder;
